import {
  Box,
  BoxProps,
  ColorOptions,
  Icon,
  ModalDialogNew,
  ModalTypes,
  RadioButton,
  Text,
  TextProps,
} from '@jfs/ui-new'
import * as React from 'react'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { FlatList, Platform } from 'react-native'
import { SvgImage } from '../../../comps/SvgImage'
import { TextInputWithLabelOnBorder } from '../../../comps/TextInputWithLabelOnBorder'
import { Colors, SnakeCase } from '../../../util/util'
export interface DropDownData {
  text?: string
  imageUrl?: string
}

export interface DropdownProps {
  ID?: string
  error?: string
  editable?: boolean
  data: Array<DropDownData>
  onChange?: (value: string) => void
  touched?: boolean
  value?: string
  label?: string
  placeHolder?: string
  isModalSizeFixed?: boolean
  modalPosition?: keyof typeof ModalTypes
  getSelectedItemData?: (value: any) => void
  type?: 'Material' | 'Normal'
  testID?: string
  onChangeText?: (text: string) => void
}

export interface RefCalback {
  isEditable(): boolean
  focus(): void
}

const MIN_DATA_TO_SHOW = 10

const RenderItem = ({ item, index, checkedItem, onHandlePress }: any) => {
  const handlePress = () => onHandlePress(item)
  const title = item?.text ?? ''

  return (
    <Box
      {...styles.container}
      onPress={handlePress}
      underlayColor={'TRANSPARENT'}
      testID={`dropdownListItemId${index}`}>
      <Box flexDirection="row" align="center" justify="space-between" width={'100%'}>
        <Box>
          <Text fontFamily="JioType-Medium" fontSize={14} color={Colors.BLACK_21 as ColorOptions}>
            {title}
          </Text>
        </Box>
        <RadioButton checked={item.text === checkedItem} size={20} onChange={handlePress} />
      </Box>
    </Box>
  )
}
export const Dropdown = forwardRef((props: DropdownProps, ref: React.Ref<RefCalback>) => {
  const {
    data,
    touched,
    editable = true,
    isModalSizeFixed = false,
    value,
    error,
    onChange,
    label,
    placeHolder = `Select ${props.label}`,
    modalPosition = 'Bottom',
    getSelectedItemData,
    type = 'Normal',
    onChangeText,
  } = props
  let modifiedValue = value
  let isPlaceHolder = false
  if (type === 'Normal' && !modifiedValue) {
    modifiedValue = placeHolder
    isPlaceHolder = true
  }

  const isLargeScreen = false

  const [errors, setErrors] = useState('')
  const [isTouched, setIsTouched] = useState(false)
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')

  useImperativeHandle(ref, () => ({
    isEditable: () => {
      return true
    },
    focus: () => handleOpen(true),
  }))

  const handleOpen = (isOpen: boolean) => {
    // If the list length is more than zero then only will show modail
    if (data.length > 0) {
      setOpen(isOpen)
    } else if (data.length === 0 && error) {
      // If the list length is zero then showing error message
      setErrors(error)
      setIsTouched(true)
    }
  }

  const onHandlePress = (item: DropDownData) => {
    if (onChangeText) {
      onChangeText(item?.text ?? '')
    }

    if (onChange) {
      // Once we selected the item and comeback to drown we need to clear the serch text
      setSearchText('')
      onChange(item?.text ?? '')
    }
    if (getSelectedItemData && item) {
      getSelectedItemData(item)
    }

    handleOpen(false)
  }

  const getListData = () => {
    const searchfn = (item: DropDownData) => {
      const search = searchText.toLowerCase()
      const text = item?.text?.toLowerCase() ?? ''
      return text.includes(search)
    }
    const listData = data.filter(searchfn)
    return listData
  }

  const onChangeTextSearch = (text: string) => {
    setSearchText(text)
  }

  const rightComponent = <Icon id="ARROW_DOWN" size={22} color="PRIMARY" />

  const renderEmptyView = () => {
    return (
      <Box {...styles.emptyContainer(isLargeScreen)}>
        <Text>{SnakeCase('Search not found')}</Text>
      </Box>
    )
  }
  return (
    <Box
      onPress={() => {
        if (editable) {
          handleOpen(true)
        }
      }}
      marginVertical={16}
      underlayColor={'TRANSPARENT'}
      width={'100%'}
      pointerEvents={Platform.OS === 'web' ? 'box-only' : 'auto'}
      testID={'dropdownId'}>
      <TextInputWithLabelOnBorder
        label={label}
        error={error || errors} // Will update the errors value if the list size zero
        touched={touched || isTouched} // Will update the isTouched value if the list size zero
        editable={false}
        numberOfLines={1}
        value={modifiedValue}
        color={editable ? (isPlaceHolder ? 'SECONDARY_GREY' : 'BLACK_75') : undefined}
        rightComponent={rightComponent}
        onPressOut={() => {
          if (editable) {
            handleOpen(true)
          }
        }}
        showBottomLine={false}
        height={48}
        labelFontSize={14}
        selectionColor={Colors.PRIMARY}
        fontSize={16}
        lineHeight={20}
      />
      <ModalDialogNew
        animate={true}
        type={'bottom'}
        show={open}
        onDismiss={() => handleOpen(false)}
        backgroundColor="TRANSPARENT">
        <Box {...styles.boxContainer} bg={'RED'}>
          <Box flexDirection={'row'} alignSelf={'stretch'} alignItems={'center'} mt={10} mb={16}>
            <Box maxWidth={'80%'}>
              <Text {...styles.headerText}>
                {label?.toLocaleLowerCase()?.includes('select') ? label : `Select ${label}`}
              </Text>
            </Box>
            <Box
              flex={1}
              alignItems="flex-end"
              justifyContent={'center'}
              onPress={() => {
                handleOpen(false)
              }}
              underlayColor="TRANSPARENT">
              <SvgImage width={24} height={24} imageName="Close" />
            </Box>
          </Box>
          <Box height={data.length > MIN_DATA_TO_SHOW ? '90%' : undefined}>
            <FlatList
              keyboardShouldPersistTaps="always"
              keyboardDismissMode="on-drag"
              data={getListData()}
              keyExtractor={(_, index) => index.toString()}
              renderItem={({ item, index }) => {
                return (
                  <RenderItem item={item} index={index} checkedItem={modifiedValue} onHandlePress={onHandlePress} />
                )
              }}
              ListEmptyComponent={renderEmptyView}
            />
          </Box>
        </Box>
      </ModalDialogNew>
    </Box>
  )
})

const styles = {
  container: {
    flexDirection: 'row',
    paddingVertical: 5,
    alignItems: 'center',
  } as BoxProps,
  boxContainer: {
    backgroundColor: 'WHITE',
    padding: 24,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    width: '100%',
    zIndex: 4,
    overflow: 'auto',
  } as BoxProps,
  emptyContainer: (isLargeScreen: boolean) =>
    ({
      height: isLargeScreen ? '70vh' : undefined,
      wight: isLargeScreen ? '50vw' : '100%',
      justifyContent: 'center',
      alignItems: 'center',
    } as BoxProps),
  headerText: {
    fontSize: 24,
    lineHeight: 28,
    fontFamily: 'JioType-Black',
    color: 'BLACK_21',
  } as TextProps,
}
